import {
  Code,
  FileArchive,
  FileAudio,
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Globe,
  Image,
} from "lucide-react";

export type FileIconInfo = {
  icon: typeof FileIcon;
  color: string;
  label: string;
};

export type FileRecordIconFixture = {
  label: string;
  contentType: string;
  originType?: string | null;
};

/** MIME substrings that map to the Code icon (after image/pdf/spreadsheet/video/audio/doc checks). */
const CODE_MIME_TOKENS = [
  "json",
  "javascript",
  "typescript",
  "xml",
  "html",
  "x-python",
  "python",
  "x-sh",
  "shellscript",
  "shell-script",
  "x-go",
  "golang",
  "x-ruby",
  "ruby",
  "x-rust",
  "rust",
  "x-java",
  "x-kotlin",
  "kotlin",
  "x-php",
  "php",
  "x-csharp",
  "csharp",
  "swift",
  "x-perl",
  "perl",
  "x-lua",
  "lua",
  "x-scala",
  "scala",
  "yaml",
  "yml",
  "toml",
  "graphql",
  "wasm",
  "dockerfile",
] as const;

function isCodeMimeType(mimeType: string): boolean {
  const m = mimeType.toLowerCase();
  if (m === "text/css") return true;
  return CODE_MIME_TOKENS.some((token) => m.includes(token));
}

function isSpreadsheetMimeType(mimeType: string): boolean {
  return (
    mimeType?.includes("spreadsheet") ||
    mimeType?.includes("csv") ||
    mimeType?.includes("excel") ||
    mimeType?.includes("tab-separated") ||
    mimeType?.includes("tsv")
  );
}

/** One entry per MIME/origin branch — keep in sync when extending `getMimeTypeIcon`. */
export const FILE_RECORD_ICON_FIXTURES: ReadonlyArray<FileRecordIconFixture> = [
  { label: "PNG", contentType: "image/png" },
  { label: "JPEG", contentType: "image/jpeg" },
  { label: "GIF", contentType: "image/gif" },
  { label: "WebP", contentType: "image/webp" },
  { label: "PDF", contentType: "application/pdf" },
  {
    label: "Spreadsheet (XLSX)",
    contentType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  },
  { label: "CSV", contentType: "text/csv" },
  { label: "TSV", contentType: "text/tab-separated-values" },
  { label: "Video", contentType: "video/mp4" },
  { label: "Audio", contentType: "audio/mpeg" },
  {
    label: "Word document",
    contentType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  { label: "RTF", contentType: "application/rtf" },
  { label: "JSON", contentType: "application/json" },
  { label: "JavaScript", contentType: "text/javascript" },
  { label: "TypeScript", contentType: "text/typescript" },
  { label: "Go", contentType: "text/x-go" },
  { label: "Shell", contentType: "application/x-sh" },
  { label: "Python", contentType: "text/x-python" },
  { label: "Rust", contentType: "text/x-rust" },
  { label: "HTML", contentType: "text/html" },
  { label: "CSS", contentType: "text/css" },
  { label: "Plain text", contentType: "text/plain" },
  { label: "Markdown", contentType: "text/markdown" },
  { label: "Archive", contentType: "application/zip" },
  { label: "Generic file", contentType: "application/octet-stream" },
  {
    label: "Webpage",
    contentType: "text/markdown",
    originType: "webpage",
  },
];

/** Representative samples for the type → color mapping story (one row per semantic bucket). */
export const FILE_RECORD_ICON_COLOR_SAMPLES: ReadonlyArray<FileRecordIconFixture> =
  [
    { label: "Image", contentType: "image/png" },
    { label: "PDF", contentType: "application/pdf" },
    { label: "Spreadsheet", contentType: "text/csv" },
    { label: "Video", contentType: "video/mp4" },
    { label: "Audio", contentType: "audio/mpeg" },
    { label: "Document", contentType: "application/rtf" },
    { label: "Code", contentType: "text/typescript" },
    { label: "Text", contentType: "text/plain" },
    { label: "Archive", contentType: "application/zip" },
    { label: "File (fallback)", contentType: "application/octet-stream" },
    {
      label: "Webpage",
      contentType: "text/markdown",
      originType: "webpage",
    },
  ];

/**
 * Returns the appropriate Lucide icon component, Tailwind color class, and
 * human-readable label for a given MIME type.
 */
export function getMimeTypeIcon(mimeType: string): FileIconInfo {
  if (mimeType?.startsWith("image/"))
    return { icon: Image, color: "text-file-image", label: "Image" };
  if (mimeType === "application/pdf")
    return { icon: FileText, color: "text-file-pdf", label: "PDF" };
  if (isSpreadsheetMimeType(mimeType))
    return {
      icon: FileSpreadsheet,
      color: "text-file-spreadsheet",
      label: "Spreadsheet",
    };
  if (mimeType?.startsWith("video/"))
    return { icon: FileVideo, color: "text-file-video", label: "Video" };
  if (mimeType?.startsWith("audio/"))
    return { icon: FileAudio, color: "text-file-audio", label: "Audio" };
  if (
    mimeType?.includes("document") ||
    mimeType?.includes("word") ||
    mimeType?.includes("msword") ||
    mimeType?.includes("rtf")
  )
    return { icon: FileText, color: "text-file-document", label: "Document" };
  if (isCodeMimeType(mimeType))
    return { icon: Code, color: "text-file-code", label: "Code" };
  if (mimeType === "text/plain" || mimeType?.includes("markdown"))
    return { icon: FileText, color: "text-file-text", label: "Text" };
  if (
    mimeType?.includes("zip") ||
    mimeType?.includes("compressed") ||
    mimeType?.includes("archive")
  )
    return { icon: FileArchive, color: "text-file-archive", label: "Archive" };
  return { icon: FileIcon, color: "text-file-default", label: "File" };
}

/**
 * Like `getMimeTypeIcon` but also handles file records with a URL origin.
 * Webpage origins (Firecrawl-scraped) receive a Globe icon regardless of
 * MIME type, since the content is a scraped web page, not a raw file.
 */
export function getFileRecordIconInfo(
  mimeType: string,
  originType?: string | null,
): FileIconInfo {
  if (originType === "webpage") {
    return { icon: Globe, color: "text-file-webpage", label: "Webpage" };
  }
  return getMimeTypeIcon(mimeType);
}
